import React, { useState, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, Car, Truck, Zap, Star, Users, Compass } from '../ui/Icons';
import { ProgressBar } from './ProgressBar';

// Import Steps
import { StepHook } from './steps/StepHook';
import { GenericChoiceStep } from './steps/GenericChoiceStep';
import { StepTradeDetails } from './steps/StepTradeDetails';
import { StepContact } from './steps/StepContact';
import { StepConfirm } from './steps/StepConfirm';

const TOTAL_STEPS = 12;

export function LeadFunnel() {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [formData, setFormData] = useState({});

  const updateData = useCallback((newData) => {
    setFormData(prev => ({ ...prev, ...newData }));
  }, []);

  const nextStep = (selectionValue) => {
    setDirection(1);
    setStep(currentStep => {
      // Step 5: Trade-in logic
      const tradeChoice = currentStep === 5 ? selectionValue : formData.hasTrade;
      if (currentStep === 5 && tradeChoice === 'No') {
        return 7; // Skip Step 6 (Trade Details)
      }
      
      // Step 8: Pre-approval logic
      const preApprovalChoice = currentStep === 8 ? selectionValue : formData.preApproval;
      if (currentStep === 8 && (preApprovalChoice === 'Already Pre-Approved' || preApprovalChoice === 'Not Right Now')) {
        return 10; // Skip Step 9 (Credit Score) and go to Step 10 (Contact)
      }
      
      return Math.min(currentStep + 1, TOTAL_STEPS - 1);
    });
  };

  const prevStep = () => {
    setDirection(-1);
    setStep(currentStep => {
      // Backwards skip for Trade-in
      if (currentStep === 7 && formData.hasTrade === 'No') {
        return 5;
      }
      // Backwards skip for Pre-approval
      if (currentStep === 10 && (formData.preApproval === 'Already Pre-Approved' || formData.preApproval === 'Not Right Now')) {
        return 8;
      }
      return Math.max(currentStep - 1, 0);
    });
  };

  const submitLead = async () => {
    // Prepare payload
    const finalPayload = {
      ...formData,
      vehicleTypes: Array.isArray(formData.vehicleTypes) ? formData.vehicleTypes.join(',') : formData.vehicleTypes,
      source: 'Vehicle Search Funnel',
      submittedAt: new Date().toLocaleString(),
    };

    // Transition UI to confirmation immediately for best UX
    setDirection(1);
    setStep(TOTAL_STEPS - 1);

    try {
      // Updated Webhook URL
      await fetch("https://connect.pabbly.com/webhook-listener/webhook/IjU3NjYwNTY5MDYzNDA0Mzc1MjY5NTUzZCI_3D_pc/IjU3NjcwNTY5MDYzMzA0MzE1MjZhNTUzNTUxMzQi_pc", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(finalPayload),
      });
      console.log("Lead successfully transmitted to new webhook.");
    } catch (error) {
      console.error("Webhook submission error:", error);
    }
  };

  const variants = {
    enter: (dir) => ({ x: dir > 0 ? 20 : -20, opacity: 0 }),
    center: { zIndex: 1, x: 0, opacity: 1 },
    exit: (dir) => ({ zIndex: 0, x: dir < 0 ? 20 : -20, opacity: 0 })
  };

  const renderStep = () => {
    switch (step) {
      case 0: return <StepHook onNext={nextStep} />;
      case 1: return <GenericChoiceStep question="New or pre-owned?" options={['New', 'Pre-Owned', 'Show Me Both']} value={formData.condition} onChange={(v) => updateData({ condition: v })} onNext={nextStep} />;
      case 2: return <GenericChoiceStep question="Which fits your lifestyle?" options={[
        { label: 'Sedan', value: 'Sedan', icon: Car },
        { label: 'SUV', value: 'SUV', icon: Compass },
        { label: 'Truck', value: 'Truck', icon: Truck },
        { label: 'EV/Hybrid', value: 'EV', icon: Zap },
        { label: 'Luxury', value: 'Luxury', icon: Star },
        { label: 'Minivan', value: 'Minivan', icon: Users },
      ]} value={formData.vehicleTypes} onChange={(v) => updateData({ vehicleTypes: v })} onNext={nextStep} />;
      case 3: return <GenericChoiceStep question="Any brands you're drawn to?" options={['Acura', 'Toyota / Honda', 'Ford / Chevy', 'BMW / Mercedes', 'Kia / Hyundai', 'Show me anything']} value={formData.preferredMake} onChange={(v) => updateData({ preferredMake: v })} onNext={nextStep} />;
      case 4: return <GenericChoiceStep question="Ideal monthly payment?" options={['Under $400', '$400–$600', '$600–$800', '$800–$1,000', '$1,000+', 'Paying Cash']} value={formData.budget} onChange={(v) => updateData({ budget: v })} onNext={nextStep} />;
      case 5: return <GenericChoiceStep question="Do you have a trade-in?" options={['Yes', 'Maybe', 'No']} value={formData.hasTrade} onChange={(v) => updateData({ hasTrade: v })} onNext={nextStep} />;
      case 6: return <StepTradeDetails data={formData} updateData={updateData} onNext={nextStep} />;
      case 7: return <GenericChoiceStep question="Planning to purchase?" options={['This Week', 'Within 2 Weeks', 'This Month', '1–3 Months', 'Just Browsing']} value={formData.timeline} onChange={(v) => updateData({ timeline: v })} onNext={nextStep} />;
      case 8: return <GenericChoiceStep question="Want to get pre-approved?" subtitle="Saves time & gives you power." options={['Yes, Get Pre-Approved', 'Already Pre-Approved', 'Not Right Now']} value={formData.preApproval} onChange={(v) => updateData({ preApproval: v })} onNext={nextStep} />;
      case 9: return <GenericChoiceStep question="How is your credit?" options={['Excellent (720+)', 'Good (660–719)', 'Fair (600–659)', 'Below 600', 'Not Sure']} value={formData.credit} onChange={(v) => updateData({ credit: v })} onNext={nextStep} />;
      case 10: return <StepContact data={formData} updateData={updateData} onSubmit={submitLead} />;
      case 11: return <StepConfirm data={formData} />;
      default: return null;
    }
  };

  return (
    <div className="h-[100dvh] flex flex-col relative overflow-hidden bg-dark-bg">
      <div className="fixed inset-0 z-[-1] overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-brand-darkBlue/10 blur-[120px] rounded-full" />
      </div>

      <ProgressBar currentStep={step} totalSteps={TOTAL_STEPS} />

      <div className="w-full max-w-5xl mx-auto px-4 h-12 flex items-center shrink-0 z-10">
        {step > 0 && step < TOTAL_STEPS - 1 && (
          <button 
            onClick={prevStep}
            className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-white transition-colors"
          >
            <ArrowLeft size={12} /> Back
          </button>
        )}
      </div>

      <main className="flex-1 flex flex-col items-center justify-center px-4 w-full max-w-4xl mx-auto relative z-10 overflow-hidden pb-4">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="w-full h-full flex flex-col justify-center"
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}